import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ValidatorFn,
  FormControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BotGuild,
  CustomEmoji,
  CustomSignupTemplate,
  SignupGroupTemplate,
} from 'src/app/models/bot/guild';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { GuildService } from 'src/app/services/guild.service';
import { ToastrService } from 'ngx-toastr';
import { EmbedBuilderComponent } from '../embed-builder/embed-builder.component';
import { EmbedService } from 'src/app/services/embed.service';
import { EmojiComponentService } from 'src/app/services/emojiComponent.service';
import { DiscordEmbed } from '../embed-builder/embed.model';
import { forkJoin, shareReplay } from 'rxjs';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-signup-group-builder',
  templateUrl: './signup-group-builder.component.html',
  styleUrls: ['../../shared/styles/action-buttons.css', './signup-group-builder.component.css'],
})
export class SignupGroupBuilderComponent
  extends EmbedBuilderComponent
  implements OnInit
{
  guild: BotGuild | undefined;
  guildId: string;
  signupGroupForm: FormGroup;
  signupTemplates: CustomSignupTemplate[] = [];
  members: any[] = [];
  parsedEmojis: any[] = [];
  selectedSignupTemplate: CustomSignupTemplate | undefined;
  currEmbed: Partial<DiscordEmbed> = {};
  filteredMembers: any[] = [];
  memberFilter = new FormControl('');
  @Input() signupGroup: SignupGroupTemplate;
  @Input() serverCustomEmojis: CustomEmoji[];
  signupGroupId: string;

  faIcons = {
    trash: faTrashAlt,
  };

  constructor(
    embedService: EmbedService,
    private fb: FormBuilder,
    private emojiComponentService: EmojiComponentService,
    private route: ActivatedRoute,
    private guildService: GuildService,
    private botService: BotResourceService,
    private toastr: ToastrService,
    private router: Router
  ) {
    super(embedService);
  }

  ngOnInit() {
    this.signupGroupId = this.route.snapshot.paramMap.get('signupGroupId');
    this.guildId = this.route.parent.snapshot.paramMap.get('id');
    this.embedService.resetEmbed();
    this.loadInitialData();
  }

  loadInitialData() {
    forkJoin({
      members: this.botService.getGuildMembers(this.guildId),
      templates: this.botService.getSignupTemplates(this.guildId),
      guildData: this.guildService.getGuildData(this.guildId),
    }).subscribe(({ members, templates, guildData }) => {
      this.members = members.sort((a, b) =>
        a.display_name.localeCompare(b.display_name)
      );
      this.filteredMembers = this.members;
      this.signupTemplates = templates;
      this.guild = guildData;

      this.memberFilter.valueChanges.subscribe((search: string) => {
        const lowerSearch = search?.toLowerCase() ?? '';
        this.filteredMembers = this.members.filter((m) =>
          m.display_name.toLowerCase().includes(lowerSearch)
        );
      });

      this.initForm();
    });
  }

  // init form with members does not work
  initForm() {
    this.signupGroupForm = this.fb.group({
      signupGroupTitle: [
        this.signupGroup.signupGroupTitle,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.minLength(1),
        ],
      ],
      signupTemplate: ['', Validators.required],
      userSelections: this.fb.group({}),
    });

    this.signupGroupForm
      .get('signupTemplate')
      .valueChanges.subscribe((templateId) => {
        this.embedService.resetEmbed();
        this.selectedSignupTemplate = this.signupTemplates.find(
          (st) => st.identifier === templateId
        );

        if (this.selectedSignupTemplate) {
          this.parseEmojis(this.selectedSignupTemplate);
          this.createUserSelectionControls();

          // ✅ After creating controls, patch in values
          if (this.signupGroup.userSelections) {
            const userSelections = this.signupGroup.userSelections;
            const userSelectionsGroup = this.signupGroupForm.get(
              'userSelections'
            ) as FormGroup;

            for (const [key, value] of Object.entries(userSelections)) {
              const parts = key.split(',');
              let fieldName = parts[2]; // main fieldName
              if (parts.length === 4) {
                fieldName = parts[3]; // subFieldName if present
              }

              const controlName = this.generateControlName(fieldName);
              if (userSelectionsGroup.contains(controlName)) {
                userSelectionsGroup.get(controlName).setValue(value);
              }
            }
          }
        } else {
          this.toastr.error('Invalid signup template selected.', 'Error');
        }
      });

    if (this.signupGroup.signupTemplate) {
      this.signupGroupForm
        .get('signupTemplate')
        .setValue(this.signupGroup.signupTemplate);
    }

    this.signupGroupForm.get('userSelections')?.valueChanges.subscribe(() => {
      this.updateEmbedPreview();
    });
  }

  private groupLevelEmojiLimitValidator(): ValidatorFn {
    return (group: FormGroup) => {
      const errors = {};

      this.parsedEmojis.forEach((emoji) => {
        if (emoji.subEmojis.length > 0 && emoji.limit > 0) {
          // Collect subemoji counts
          let totalSelected = 0;
          emoji.subEmojis.forEach((subEmoji) => {
            const control = group.get(subEmoji.controlName);
            if (control && Array.isArray(control.value)) {
              totalSelected += control.value.length;
            }
          });

          // Check if total exceeds the main emoji limit
          if (totalSelected > emoji.limit) {
            // Attach error to all subemoji controls
            emoji.subEmojis.forEach((subEmoji) => {
              const control = group.get(subEmoji.controlName);
              if (control) {
                control.setErrors({
                  ...(control.errors || {}),
                  groupLimitExceeded: {
                    limit: emoji.limit,
                    totalSelected,
                    field: emoji.fieldName,
                  },
                });
              }
            });
            errors[emoji.controlName] = {
              groupLimitExceeded: true,
              totalSelected,
              limit: emoji.limit,
            };
          }
        }
      });

      // Attach group-level errors if any
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  canSelectMore(controlName: string): boolean {
    const control = this.userSelections.get(controlName);
    if (!control || !Array.isArray(control.value)) return true;

    // Find the parent emoji group and the target control
    const emoji = this.parsedEmojis.find((e) => {
      return (
        e.controlName === controlName ||
        e.subEmojis?.some((sub) => sub.controlName === controlName)
      );
    });

    if (!emoji) return true;

    const isMain = emoji.controlName === controlName;

    // If the main emoji has a limit > 0, apply group-level limit
    if (emoji.limit > 0) {
      const totalSelected = [emoji, ...(emoji.subEmojis || [])]
        .map((e) => this.userSelections.get(e.controlName)?.value || [])
        .reduce((acc, val) => acc + val.length, 0);

      return totalSelected < emoji.limit || control.value.includes(controlName);
    }

    // If we're in a sub-emoji and it has its own limit
    const subEmoji = emoji.subEmojis?.find(
      (sub) => sub.controlName === controlName
    );
    const subLimit = subEmoji?.limit || 0;

    if (subLimit === 0) return true;

    const selected = control.value?.length || 0;
    return selected < subLimit || control.value.includes(controlName);
  }

  private convertEmojisObjectToArray(emojisObject: Record<string, any>): any[] {
    return Object.keys(emojisObject).map((mainEmojiKey) => {
      const mainEmoji = emojisObject[mainEmojiKey];
      if (!mainEmoji.field_name) return [];
      const controlName = this.generateControlName(mainEmoji.field_name);

      const subEmojis = mainEmoji.emojis
        ? Object.keys(mainEmoji.emojis).map((subEmojiKey) => {
            const subEmoji = mainEmoji.emojis[subEmojiKey];
            return {
              emoji: subEmojiKey,
              fieldName: subEmoji.field_name,
              controlName: this.generateControlName(subEmoji.field_name),
            };
          })
        : [];

      return {
        emoji: mainEmojiKey,
        fieldName: mainEmoji.field_name,
        controlName,
        type: mainEmoji.type,
        subEmojis,
      };
    });
  }

  get userSelections(): FormGroup {
    return this.signupGroupForm.get('userSelections') as FormGroup;
  }

  private buildSelectedMembersMap(useFullObject = true): Record<string, any[]> {
    const userSelections = this.userSelections;
    const selectedMembersMap: Record<string, any[]> = {};

    this.parsedEmojis.forEach((emoji) => {
      selectedMembersMap[emoji.controlName] = [];
      if (!emoji.subEmojis?.length) {
        selectedMembersMap[emoji.controlName] = this.getSelectedMembers(
          userSelections.get(emoji.controlName)?.value || [],
          useFullObject
        );
      } else {
        emoji.subEmojis.forEach((subEmoji) => {
          selectedMembersMap[subEmoji.controlName] = [];
          selectedMembersMap[subEmoji.controlName] = this.getSelectedMembers(
            userSelections.get(subEmoji.controlName)?.value || [],
            useFullObject
          );
        });
      }
    });

    return selectedMembersMap;
  }

  private getSelectedMembers(ids: string[], fullObject: boolean): any[] {
    return ids
      .map((id: string) => {
        const member = this.members.find(
          (m) => m.user_id === id || m.id === id
        );
        if (!member) return null;
        return fullObject
          ? { user_id: member.user_id, display_name: member.display_name }
          : member.display_name;
      })
      .filter(Boolean);
  }

  updateEmbedPreview(): void {
    const selectedMembersMap = this.buildSelectedMembersMap(true);

    this.emojiComponentService.signupTemplateEmojisToEmbedFields(
      this.embedService,
      this.serverCustomEmojis,
      { currEmbed: this.currEmbed },
      {
        emojis: this.convertEmojisObjectToArray(
          this.selectedSignupTemplate.emojis
        ),
        emojiBehavior: this.selectedSignupTemplate.emoji_behavior.toString(),
        useButtons: this.selectedSignupTemplate.use_buttons,
        color: this.selectedSignupTemplate.color,
        emojiTitle: this.selectedSignupTemplate.emoji_title,
      },
      selectedMembersMap,
      this.members
    );
  }

  parseEmojis(signupTemplate: CustomSignupTemplate) {
    this.parsedEmojis = [];
    const emojis = signupTemplate.emojis;
    const limits = signupTemplate.limits;

    for (const [emojiKey, emojiValue] of Object.entries(emojis)) {
      if (!emojiValue.field_name) {
        // Skip emoji if field_name is null. Example: late, rlate, remove
        continue;
      }

      const mainEmoji = {
        key: emojiKey,
        fieldName: emojiValue.field_name,
        type: emojiValue.type,
        controlName: this.generateControlName(emojiValue.field_name),
        limit: limits[`role,${emojiValue.field_name}`] || 0,
        subEmojis: [],
      };

      if (emojiValue.emojis) {
        for (const [subEmojiKey, subEmojiValue] of Object.entries(
          emojiValue.emojis
        )) {
          if (
            typeof subEmojiValue === 'object' &&
            subEmojiValue !== null &&
            'field_name' in subEmojiValue
          ) {
            const subFieldName = subEmojiValue.field_name;
            const subEmoji = {
              key: subEmojiKey,
              fieldName: subFieldName,
              controlName: this.generateControlName(subFieldName),
              limit:
                limits[`role,${emojiValue.field_name},${subFieldName}`] || 0,
            };
            mainEmoji.subEmojis.push(subEmoji);
          } else {
            console.warn(
              `Skipping malformed sub-emoji: ${subEmojiKey}`,
              subEmojiValue
            );
          }
        }
      }

      this.parsedEmojis.push(mainEmoji);
    }
  }

  createUserSelectionControls() {
    const userSelections = this.signupGroupForm.get(
      'userSelections'
    ) as FormGroup;

    // remove all controls
    Object.keys(userSelections.controls).forEach((controlName) => {
      userSelections.removeControl(controlName);
    });

    this.parsedEmojis.forEach((emoji) => {
      // Add control for main emoji if no sub-emojis
      if (!emoji.subEmojis || emoji.subEmojis.length === 0) {
        const key = `${emoji.key},${emoji.type},${emoji.fieldName}`;
        const initial = this.signupGroup?.userSelections?.[key] || [];
        userSelections.addControl(
          emoji.controlName,
          this.fb.control(initial, [this.maxLengthArrayValidator(emoji.limit)])
        );
      } else {
        // Add controls for sub-emojis
        emoji.subEmojis.forEach((sub) => {
          const key = `${sub.key},${emoji.type},${emoji.fieldName},${sub.fieldName}`;
          const initial = this.signupGroup?.userSelections?.[key] || [];
          userSelections.addControl(
            sub.controlName,
            this.fb.control(initial, [this.maxLengthArrayValidator(sub.limit)])
          );
        });
      }
    });
    userSelections.setValidators(this.groupLevelEmojiLimitValidator());

    this.updateEmbedPreview();
  }

  generateControlName(name: string): string {
    // Normalize control name (e.g., "Tank, magic" -> "tank_magic")
    return name.toLowerCase().replace(/\W+/g, '_');
  }

  maxLengthArrayValidator(max: number) {
    return (control) => {
      if (max === 0) return null; // 0 means unlimited
      return control.value && control.value.length > max
        ? {
            maxLength: {
              requiredLength: max,
              actualLength: control.value.length,
            },
          }
        : null;
    };
  }

  onSubmit(): void {
    if (this.signupGroupForm.invalid) {
      this.toastr.error(
        'Form has errors. Please review all selections.',
        'Validation Error'
      );
      return;
    }

    const userSelections = this.userSelections;
    const outputSelections: Record<string, string[]> = {};

    for (const emoji of this.parsedEmojis) {
      const { key: emojiKey, type, fieldName, controlName, subEmojis } = emoji;

      if (!subEmojis?.length) {
        const selected: string[] = userSelections.get(controlName)?.value || [];
        const key = `${emojiKey},${type},${fieldName}`;
        outputSelections[key] = selected;
      } else {
        for (const sub of subEmojis) {
          const selected: string[] =
            userSelections.get(sub.controlName)?.value || [];
          const key = `${sub.key},${type},${fieldName},${sub.fieldName}`;
          outputSelections[key] = selected;
        }
      }
    }

    const payload = {
      signupGroupTitle: this.signupGroupForm.get('signupGroupTitle').value,
      signupTemplate: this.signupGroupForm.get('signupTemplate').value,
      userSelections: outputSelections,
    };

    if (this.signupGroupId === null) {
      this.botService.createSignupGroup(this.guildId, payload).subscribe({
        next: (response: any) => {
          if (response.status === 201) {
            const templateId = response.body.id;
            this.guild.signup_groups.push({
              id: templateId,
              name: response.body.name,
            });
            this.router.navigate([
              'guilds',
              this.guildId,
              'signup-group',
              templateId,
            ]);
          }
        },
        error: (error: any) => {
          console.error('Error creating template:', error);
        },
      });
    } else {
      this.botService.updateSignupGroup(this.guildId, this.signupGroupId, payload).subscribe({
        next: () => {
          this.guild.signup_groups.find((sg) => sg.id === this.signupGroupId).name = this.signupGroupForm.get('signupGroupTitle').value
        },
        error: (error: any) => {
          console.error('Error updating template:', error);
        },
      });
    }
  }

  deleteGroup() {
    this.botService.deleteSignupGroup(this.guildId, this.signupGroupId).subscribe({
      next: () => {
        this.guild.signup_groups = this.guild.signup_groups.filter(template => template.id !== this.signupGroupId);
        this.router.navigate([`/guilds/${this.guildId}/signup-group/new`]);
      },
      error: (error: any) => {
        console.error('Error deleting template:', error);
        // Add error handling logic here, like showing an error message
      }
    })
  }
}
